export type PaywallFilters = {
    access: "all" | "free" | "paid" | "scheduled";
    status: "all" | "published" | "draft" | "scheduled";
    from?: number;
    to?: number;
    search: string;
    page: number;
    size: number;
};
export type ChapterSelection =
    | { mode: "explicit"; chapterIds: string[] }
    | { mode: "all_matching"; excludedChapterIds: string[] };
export type AccessEdit = {
    access: "free" | "premium";
    coinPrice: number;
    freeAfter: string | null;
};

export function parsePaywallFilters(input: Record<string, unknown>): PaywallFilters {
    const integer = (key: string, fallback?: number) => {
        const value = input[key];
        if (value === undefined || value === "" || value === null) return fallback;
        const parsed = Number(value);
        if (!Number.isSafeInteger(parsed) || parsed < 1 || parsed > 1_000_000) throw new Error(`Invalid ${key}`);
        return parsed;
    };
    const access = input.access ?? "all";
    const status = input.status ?? "all";
    if (!["all", "free", "paid", "scheduled"].includes(String(access))) throw new Error("Invalid access filter");
    if (!["all", "published", "draft", "scheduled"].includes(String(status))) throw new Error("Invalid status filter");
    const from = integer("from");
    const to = integer("to");
    if (from && to && from > to) throw new Error("The last chapter must be at least the first chapter");
    const size = integer("size", 50)!;
    if (![25, 50, 100].includes(size)) throw new Error("Invalid page size");
    if (input.search !== undefined && typeof input.search !== "string") throw new Error("Invalid search");
    const search = String(input.search ?? "").trim();
    if (search.length > 200) throw new Error("Search must be 200 characters or fewer");
    return { access: access as PaywallFilters["access"], status: status as PaywallFilters["status"], from, to, size, search, page: integer("page", 1)! };
}

export function toggleChapterSelection(selection: ChapterSelection, id: string): ChapterSelection {
    const ids = new Set(selection.mode === "explicit" ? selection.chapterIds : selection.excludedChapterIds);
    if (ids.has(id)) ids.delete(id); else ids.add(id);
    return selection.mode === "explicit" ? { mode: "explicit", chapterIds: [...ids] } : { mode: "all_matching", excludedChapterIds: [...ids] };
}

export function selectedCount(selection: ChapterSelection, total: number) {
    return selection.mode === "explicit" ? selection.chapterIds.length : Math.max(0, total - selection.excludedChapterIds.length);
}

export function paywallQuery(filters: PaywallFilters) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== "") params.set(key, String(value));
    }
    return params.toString();
}
